import { BASE_URL } from '@constants/api'
import {
	AiGeneration,
	AiGenerationCreateRequest,
	AiGenerationDetail,
	AiGenerationHistoryResponse,
	AiGenerationListRequest,
	AiGenerationListResponse,
	AiGenerationQuestionsResponse,
} from '@models/aiGeneration.model'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { setAuthorization } from '@utils/helpers'

export const aiGenerationApi = createApi({
	reducerPath: 'aiGenerationApi',
	tagTypes: ['AiGenerations', 'AiGeneration'],
	baseQuery: fetchBaseQuery({
		baseUrl: BASE_URL,
		prepareHeaders: setAuthorization,
	}),
	endpoints: (builder) => ({
		list: builder.query<AiGenerationListResponse, AiGenerationListRequest>({
			query: ({ page = 1, limit = 20, status = '', search = '' }) => ({
				url: `/ai/generations?page=${page}&limit=${limit}&status=${status}&search=${encodeURIComponent(
					search
				)}`,
				method: 'GET',
			}),
			providesTags: ['AiGenerations'],
		}),

		show: builder.query<AiGenerationDetail, { id: number | string }>({
			query: ({ id }) => ({
				url: `/ai/generations/${id}`,
				method: 'GET',
			}),
			providesTags: ['AiGeneration'],
		}),

		history: builder.query<
			AiGenerationHistoryResponse,
			{ id: number | string }
		>({
			query: ({ id }) => ({
				url: `/ai/generations/${id}/history`,
				method: 'GET',
			}),
		}),

		questions: builder.query<
			AiGenerationQuestionsResponse,
			{ id: number | string; page?: number; limit?: number }
		>({
			query: ({ id, page = 1, limit = 20 }) => ({
				url: `/ai/generations/${id}/questions?page=${page}&limit=${limit}`,
				method: 'GET',
			}),
			providesTags: ['AiGeneration'],
		}),

		create: builder.mutation<AiGeneration, AiGenerationCreateRequest>({
			query: (data) => {
				const formData = new FormData()

				formData.append('examPdf', data.examPdf[0])
				formData.append('answerKeyPdf', data.answerKeyPdf[0])

				if (data.answerKeyVariant) {
					formData.append('answerKeyVariant', data.answerKeyVariant)
				}

				data.filterIds?.forEach((id) => {
					formData.append('filterIds[]', String(id))
				})

				data.scopes?.forEach((scope) => {
					formData.append('scopes[]', scope)
				})

				return {
					url: `/ai/generations`,
					method: 'POST',
					body: formData,
				}
			},
			invalidatesTags: ['AiGenerations'],
		}),

		cancel: builder.mutation<AiGeneration, { id: number | string }>({
			query: ({ id }) => ({
				url: `/ai/generations/${id}/cancel`,
				method: 'POST',
			}),
			invalidatesTags: ['AiGenerations', 'AiGeneration'],
		}),

		retry: builder.mutation<AiGeneration, { id: number | string }>({
			query: ({ id }) => ({
				url: `/ai/generations/${id}/retry`,
				method: 'POST',
			}),
			invalidatesTags: ['AiGenerations', 'AiGeneration'],
		}),
	}),
})
